import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import { useAuth, useCurrentUser } from '@/auth/AuthContext';
import { BrandMark } from '@/components/layout/Brand';
import { Button, useToast } from '@/components/ui';
import { ChangePasswordForm } from '@/features/settings/ChangePasswordForm';

/**
 * Shown after sign-in when a Super Admin created the account or reset its
 * password. The API refuses every other request until the change is made.
 */
export function ForcedPasswordChangePage() {
  const user = useCurrentUser();
  const { signOut } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [saving, setSaving] = useState(false);
  const from = (location.state as { from?: string } | null)?.from ?? '/categories';

  if (!user.mustChangePassword) return <Navigate to={from} replace />;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 py-10">
      <main className="w-full max-w-[420px]">
        <div className="card px-8 py-9">
          <div className="mb-6 flex flex-col items-center text-center">
            <BrandMark size={44} />
            <h1 className="mt-4 flex items-center gap-2 text-xl font-semibold text-ink">
              <KeyRound size={18} aria-hidden="true" className="text-primary" />
              Choose a New Password
            </h1>
            <p className="mt-1.5 text-[13px] text-ink-secondary">
              Your account ({user.email}) was given a temporary password by a Super Admin. Choose your own password to continue.
            </p>
          </div>
          <ChangePasswordForm
            formId="forced-password-form"
            forced
            onSavingChange={setSaving}
            onChanged={() => {
              notify({ tone: 'success', title: 'Password changed', description: 'Your new password is now active.' });
              navigate(from === '/change-password' ? '/categories' : from, { replace: true });
            }}
          />
          <div className="mt-6 grid gap-2">
            <Button type="submit" form="forced-password-form" size="lg" className="w-full" loading={saving}>
              Set New Password
            </Button>
            <Button variant="ghost" className="w-full" disabled={saving} onClick={() => void signOut()}>
              Sign Out
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
